
import React from 'react';
import Hero from '../components/Hero.tsx';
import Services from '../components/Services.tsx';
import { Page } from '../types.ts';

interface HomeProps {
  setPage: (page: Page) => void;
}

const Home: React.FC<HomeProps> = ({ setPage }) => {
  return (
    <div className="animate-fade-in">
      <Hero setPage={setPage} />
      
      {/* Short Services Grid */}
      <section className="py-20 sm:py-28 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-16 gap-8 text-center md:text-left">
            <div className="max-w-xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 dark:text-white mb-6">Focus on your business, we handle 2026 compliance.</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400">From company incorporation to algorithmic tax filings, FinTrack is your outsourced back-office partner.</p>
            </div>
            <button 
              onClick={() => setPage('services')} 
              className="group px-6 py-3 bg-primary/5 dark:bg-white/5 rounded-full text-primary dark:text-accent-400 font-bold inline-flex items-center gap-2 hover:bg-primary hover:text-white transition-all duration-300"
            >
              Explore all services 
              <span className="material-symbols-rounded group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </button>
          </div>
          <Services limit={3} setPage={setPage} />
        </div>
      </section>

      {/* Feature Split - Built for Scale */}
      <section className="py-20 sm:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-accent rounded-full blur-3xl opacity-30 animate-pulse"></div>
              <div className="relative rounded-[40px] sm:rounded-[60px] overflow-hidden shadow-3xl z-10">
                <img 
                  src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=1000" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-[3s]"
                  alt="Professional financial meeting"
                />
              </div>
              {/* Floating Stat Card */}
              <div className="absolute -bottom-10 -right-4 sm:-right-10 bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-[32px] shadow-2xl border border-slate-100 dark:border-slate-700 animate-float hidden sm:block">
                <p className="text-4xl font-display font-black text-primary dark:text-accent-400 mb-1">99.8%</p>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">2026 Success Rate</p>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 text-center lg:text-left">
              <h3 className="text-xs sm:text-sm font-black uppercase tracking-[0.3em] text-primary mb-6">Engineered for the 2026 Economy</h3>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 dark:text-white mb-8 leading-tight">Your company is only as strong as its foundation.</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-10 leading-relaxed">
                In Nigeria, compliance isn't just a legal requirement; it's a competitive advantage. FinTrack ensures you never miss a 2026 FIRS deadline or a CAC PSC-registry filing, keeping your record clean for local and international investors.
              </p>
              
              <div className="space-y-5 mb-12 max-w-md mx-auto lg:mx-0">
                {[
                  'Real-time 2026 legislative monitoring',
                  'Automated TIN-NIN harmonization alerts',
                  'Direct access to Senior Chartered Accountants'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 text-left font-semibold text-slate-700 dark:text-slate-300 group">
                    <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-white transition-all">
                      <span className="material-symbols-rounded text-sm">check</span>
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={() => setPage('booking')} 
                className="btn-premium px-10 py-4 sm:py-5 bg-primary text-white rounded-2xl font-bold shadow-2xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3"
              >
                Schedule 2026 Strategy Session
                <span className="material-symbols-rounded">calendar_month</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
